import * as XLSX from 'xlsx';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface ExcelColumn<T = Record<string, unknown>> {
  key: keyof T;
  label: string;
  width?: number;
  format?: (value: unknown) => string | number;
}

export interface ExportOptions<T = Record<string, unknown>> {
  data: T[];
  columns: ExcelColumn<T>[];
  fileName?: string;
  sheetName?: string;
  freezeHeader?: boolean;
  autoWidth?: boolean;
}

export interface MultiSheetExportOptions {
  sheets: Array<{
    data: Record<string, unknown>[];
    columns: ExcelColumn[];
    sheetName: string;
  }>;
  fileName?: string;
}

// ─────────────────────────────────────────────
// Auto-detect: infer formatter from key name
// ─────────────────────────────────────────────

const DATE_KEYWORDS = [
  'created',
  'updated',
  'date',
  'dob',
  'birthday',
  'expiry',
  'expires',
  'modified',
  'timestamp',
];
const CURRENCY_KEYWORDS = [
  'amt',
  'amount',
  'price',
  'cost',
  'fee',
  'charge',
  'revenue',
  'salary',
];

/**
 * Returns an auto-formatter based on the column key name.
 * Matches are case-insensitive and substring-based.
 * Returns null if no match — raw value is used as-is.
 */
function inferFormatter(
  key: string
): ((value: unknown) => string | number) | null {
  const lower = key.toLowerCase();
  if (DATE_KEYWORDS.some((kw) => lower.includes(kw))) return formatDate;
  if (CURRENCY_KEYWORDS.some((kw) => lower.includes(kw))) return formatCurrency;
  return null;
}

// ─────────────────────────────────────────────
// Core: build a worksheet from data + columns
// ─────────────────────────────────────────────

function buildWorksheet<T = Record<string, unknown>>(
  data: T[],
  columns: ExcelColumn<T>[],
  autoWidth = true,
  freezeHeader = true
): XLSX.WorkSheet {
  // Map data rows to display-label keyed objects
  const rows = data.map((row) =>
    Object.fromEntries(
      columns.map((col) => {
        const raw = (row as Record<string, unknown>)[col.key as string];
        const formatter = col.format ?? inferFormatter(col.key as string);
        const value = formatter ? formatter(raw) : (raw ?? '');
        return [col.label, value];
      })
    )
  );

  const ws = XLSX.utils.json_to_sheet(rows);

  // Auto column widths
  if (autoWidth) {
    ws['!cols'] = columns.map((col) => {
      const formatter = col.format ?? inferFormatter(col.key as string);
      return {
        wch:
          Math.max(
            col.width ?? 0,
            col.label.length,
            ...data.map((row) => {
              const raw = (row as Record<string, unknown>)[col.key as string];
              const val = formatter ? formatter(raw) : raw;
              return String(val ?? '').length;
            })
          ) + 2, // padding
      };
    });
  }

  // Freeze header row
  if (freezeHeader) {
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  }

  return ws;
}

// ─────────────────────────────────────────────
// Export: static columns or dynamic grid
// ─────────────────────────────────────────────

/**
 * Export a flat data array with a defined column map.
 * Works for both static column definitions and dynamic grids.
 *
 * @example
 * exportToExcel({
 *   data: users,
 *   columns: [
 *     { key: 'name', label: 'Full Name' },
 *     { key: 'email', label: 'Email' },
 *   ],
 *   fileName: 'users-report',
 * });
 */
export function exportToExcel<T = Record<string, unknown>>({
  data,
  columns,
  fileName = 'export-excel',
  sheetName = 'Sheet1',
  freezeHeader = true,
  autoWidth = true,
}: ExportOptions<T>): void {
  if (!data.length) {
    console.warn('[exportToExcel] No data to export.');
    return;
  }

  const ws = buildWorksheet(data, columns, autoWidth, freezeHeader);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${fileName}.xlsx`);
}

// ─────────────────────────────────────────────
// Export: multiple sheets in one workbook
// ─────────────────────────────────────────────

/**
 * Export multiple datasets into separate sheets within one .xlsx file.
 *
 * @example
 * exportMultiSheet({
 *   sheets: [
 *     { data: users,   columns: USER_COLS,   sheetName: 'Users'   },
 *     { data: orders,  columns: ORDER_COLS,  sheetName: 'Orders'  },
 *   ],
 *   fileName: 'full-report',
 * });
 */
export function exportMultiSheet({
  sheets,
  fileName = 'export',
}: MultiSheetExportOptions): void {
  if (!sheets.length) {
    console.warn('[exportMultiSheet] No sheets to export.');
    return;
  }

  const wb = XLSX.utils.book_new();

  sheets.forEach(({ data, columns, sheetName }) => {
    const ws = buildWorksheet(data, columns);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  XLSX.writeFile(wb, `${fileName}.xlsx`);
}

// ─────────────────────────────────────────────
// Export: raw dynamic grid (columnDefs + rowData)
// for AG Grid / TanStack or similar
// ─────────────────────────────────────────────

export interface GridExportOptions {
  rowData: Record<string, unknown>[];
  columnDefs: Array<{ field: string; headerName?: string }>;
  fileName?: string;
  sheetName?: string;
}

/**
 * Export directly from a grid's rowData + columnDefs.
 * Preserves column order and uses headerName as the Excel header.
 *
 * @example
 * exportGridToExcel({
 *   rowData:    gridRef.current.api.getRenderedNodes().map(n => n.data),
 *   columnDefs: gridRef.current.api.getColumnDefs(),
 *   fileName:   'grid-export',
 * });
 */
export function exportGridToExcel({
  rowData,
  columnDefs,
  fileName = 'grid-export',
  sheetName = 'Sheet1',
}: GridExportOptions): void {
  if (!rowData.length) {
    console.warn('[exportGridToExcel] No rows to export.');
    return;
  }

  const columns: ExcelColumn[] = columnDefs.map((col) => ({
    key: col.field,
    label: col.headerName ?? col.field,
  }));

  exportToExcel({ data: rowData, columns, fileName, sheetName });
}

// ─────────────────────────────────────────────
// Utility: format helpers for cell values
// ─────────────────────────────────────────────

/** Format a JS Date or ISO string as DD/MM/YYYY */
export function formatDate(value: unknown): string {
  if (!value) return '';
  const d = new Date(value as string);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB'); // DD/MM/YYYY
}

/** Format a number as currency string, e.g. "1,234.56" */
export function formatCurrency(value: unknown, symbol = '₹'): string {
  const num = Number(value);
  if (isNaN(num)) return '';
  return `${symbol}${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Format a decimal as percentage string, e.g. 0.25 → "25.00%" */
export function formatPercent(value: unknown, decimals = 2): string {
  const num = Number(value);
  if (isNaN(num)) return '';
  return `${(num * 100).toFixed(decimals)}%`;
}
